import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Factory, PackageCheck, Phone, Truck, Zap } from "lucide-react";
import { useLang } from "@/i18n";

const HERO_IMG = "/produkte/haus-antenne.webp";

const TRUST = [
    { icon: Factory, de: "Eigene Fertigung in Hemhofen", en: "In-house manufacturing in Hemhofen" },
    { icon: Truck, de: "Lieferung DE & EU-Ausland", en: "Delivery in DE & across the EU" },
    { icon: PackageCheck, de: "Katalogartikel ab Lager", en: "Catalogue items ex stock" },
    { icon: Zap, de: "48h Reparaturservice", en: "48h repair service" },
];

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const glow = useTransform(scrollYProgress, [0, 0.8], [1, 0.3]);
    const { t } = useLang();
    const LINES = t(
        ["Qualität in", "Satelliten- &", "Antennentechnik."],
        ["Excellence in", "Satellite &", "Antenna Technology."]
    );

    return (
        <section ref={ref} data-testid="hero" className="relative overflow-hidden pt-36 lg:pt-44 pb-16 lg:pb-20">
            <div className="absolute inset-0 bg-blueprint" aria-hidden="true" />
            <motion.div
                style={{ opacity: glow }}
                className="absolute -top-48 right-[-12%] h-[620px] w-[620px] rounded-full bg-red-100/70 blur-3xl"
                aria-hidden="true"
            />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
                    <div className="lg:col-span-7">
                        <motion.img
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.02 }}
                            src="/logo_transparent.png"
                            alt="Müller Satelliten- und Antennentechnik GmbH"
                            data-testid="hero-logo"
                            className="h-20 sm:h-24 w-auto -ml-1 mb-5 drop-shadow-sm"
                        />
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.05 }}
                            className="inline-flex items-center gap-2.5 eyebrow text-red-700 bg-white border border-red-200/70 px-3.5 py-2 rounded-sm shadow-sm"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-red-600 animate-pulse" />
                            Müller Satelliten- &amp; Antennentechnik · Hemhofen
                        </motion.div>

                        <h1 className="mt-7 font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.04] text-slate-900">
                            {LINES.map((l, i) => (
                                <span key={l} className="block overflow-hidden pb-1">
                                    <motion.span
                                        className="block"
                                        initial={{ y: "112%" }}
                                        animate={{ y: 0 }}
                                        transition={{ duration: 0.9, delay: 0.18 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                                    >
                                        {l}
                                    </motion.span>
                                </span>
                            ))}
                        </h1>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.62 }}
                            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600"
                        >
                            {t(
                                "Hersteller eigener Kabelkopfstationen (DHx-Reihe), Spezialist für SAT-ZF-Verteiltechnik und Ihr kompetenter Partner für Kabel- und SAT-Anlagen — mit 48h-Reparaturservice für Kopfstationstechnik.",
                                "Manufacturer of our own cable headends (DHx series), specialist in SAT-IF distribution and your competent partner for cable and satellite systems — with a 48h repair service for headend technology."
                            )}
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.74 }}
                            className="mt-9 flex flex-wrap items-center gap-4"
                        >
                            <a
                                href="#produkte"
                                data-testid="hero-cta-produkte"
                                className="group inline-flex items-center gap-2 rounded-full bg-red-700 hover:bg-red-800 text-white font-semibold px-7 py-3.5 transition-colors"
                            >
                                {t("Produktwelt entdecken", "Explore our products")}
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </a>
                            <a
                                href="#reparatur"
                                data-testid="hero-cta-reparatur"
                                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/70 hover:border-red-400 hover:text-red-700 text-slate-800 font-semibold px-7 py-3.5 transition-colors"
                            >
                                {t("48h Reparatur-Service", "48h Repair Service")}
                            </a>
                            <a
                                href="tel:+49919594040"
                                data-testid="hero-cta-phone"
                                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-red-700 border-b-2 border-transparent hover:border-red-600 pb-0.5 transition-colors"
                            >
                                <Phone className="h-4 w-4" />
                                +49 (0) 9195 / 9404-0
                            </a>
                        </motion.div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 44 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-5 relative"
                    >
                        <div className="relative aspect-[4/3] sm:aspect-[5/5] lg:aspect-[5/6] rounded-2xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
                            <motion.img
                                style={{ y: imgY }}
                                src={HERO_IMG}
                                alt="Normale Haus-Satellitenantenne vor blauem Himmel"
                                data-testid="hero-image"
                                className="absolute inset-0 h-full w-full object-cover scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/35 via-transparent to-transparent" aria-hidden="true" />
                            <div className="absolute top-4 right-4 font-mono text-white/80 text-xs select-none" aria-hidden="true">+</div>
                            <div className="absolute top-4 left-4 font-mono text-white/80 text-xs select-none" aria-hidden="true">+</div>
                            <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-md bg-white/90 backdrop-blur border border-slate-200/70 px-3 py-2">
                                <span className="h-1.5 w-1.5 rounded-full bg-red-600" />
                                <span className="eyebrow text-[10px] text-slate-700">{t("SAT-Anlagen · Montage & Service", "SAT systems · Installation & service")}</span>
                            </div>
                        </div>
                        <div className="mt-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
                            <span>{t("SAT-ZF · Kopfstation · Verteiltechnik", "SAT-IF · headends · distribution")}</span>
                            <span>91334 Hemhofen</span>
                        </div>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.95 }}
                    className="mt-16 lg:mt-20 border-t border-slate-200 pt-7 grid grid-cols-2 lg:grid-cols-4 gap-5"
                    data-testid="hero-trustbar"
                >
                    {TRUST.map(({ icon: Icon, de, en }) => (
                        <div key={de} className="flex items-center gap-3">
                            <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-white border border-slate-200 text-red-700 shadow-sm">
                                <Icon className="h-4 w-4" />
                            </span>
                            <span className="text-sm font-medium text-slate-700">{t(de, en)}</span>
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}
